/**
 * Replacing the system that is already there — the screen.
 *
 * An airport does not switch systems one morning. It runs the old application
 * and this one side by side and moves screens over as each earns it. The
 * platform already does that work: a contract per legacy screen, shadow
 * running, an agreement rate, a triage on every difference, a cutover guard
 * that refuses on evidence and a revert that takes one call. None of it was
 * visible in the product, so this desk is the reading of it.
 *
 * Three things it is careful about. The fraction says how many *declared*
 * screens are live, never a share of the old application's screens, because
 * nobody has counted those. The platform's own `blocking` list is the
 * authority on whether a screen may go live, and the distances beside it are
 * this desk's arithmetic, labelled as such. And a refused cutover is shown
 * with its reasons rather than as an error, because the refusal is the
 * feature.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, ErrorState, Input, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import {
  gaps,
  MODE_LABEL,
  programme,
  progress,
  ready,
  required,
  TRIAGE_LABEL,
  VERDICT_LABEL,
  type MigrationScreen,
  type MigrationStatus,
  type ScreenMode,
  type Triage,
} from "../../domain/migration";
import { locale, t } from "../../i18n";
import s from "./Migration.module.css";

const MODE_CLASS: Record<ScreenMode, string> = { live: s.live, shadow: s.shadow, off: s.off };
const MODE_TAG: Record<ScreenMode, string> = { live: "LIVE", shadow: "SHADOW", off: "OFF" };
const pct = (n: number | null) => (n == null ? "—" : `${n}%`);

export function MigrationPage() {
  const session = useSession();
  const source = session.source;
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["migration", session.site.id],
    staleTime: 30_000,
    queryFn: () => source.migrationStatus(),
  });
  const status: MigrationStatus | undefined = q.data;
  const p = status ? programme(status) : null;

  useScreenContext(
    p
      ? {
          route: "/migration",
          title: "Replacing the existing system",
          summary: [
            `${p.live} of ${p.total} declared screens are served by the platform; ${p.shadow} are shadow running and ${p.off} are not yet started.`,
            p.agreementPct == null
              ? "No shadow screen has anything comparable recorded yet."
              : `Shadow screens agree with the existing application ${p.agreementPct}% of the time over ${p.observations.toLocaleString()} recorded runs.`,
            p.untriaged
              ? `${p.untriaged} difference(s) are waiting for somebody to say which side was wrong.`
              : "Every difference has been triaged.",
          ],
          offers: [
            {
              label: "What is ready to cut over?",
              ask: "Which screens would the guard let go live today, and what is blocking the rest?",
            },
            {
              label: "What should I do next?",
              ask: "What is the next action on the migration, and why that one first?",
            },
            {
              label: "Where do the two disagree?",
              ask: "Which screens disagree with the existing application, and about what?",
            },
          ],
        }
      : null,
  );

  const mode = useMutation({
    mutationFn: (v: { key: string; mode: ScreenMode; reason: string; force?: boolean }) =>
      source.setScreenMode(v.key, v.mode, v.reason, v.force),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["migration"] }),
  });

  return (
    <Page
      eyebrow={t("{site} · platform", { site: session.site.code })}
      title="Replacing the existing system"
      actions={
        p ? (
          <span className={s.mono} style={{ fontSize: 13, color: "var(--muted)" }}>
            {t("{live} of {total} screens live", { live: p.live, total: p.total })}
          </span>
        ) : undefined
      }
    >
      <div className={s.desk}>
        <p className={s.lead}>
          {t(
            "The existing system keeps running while each screen here is tested in shadow and moved over once proven.",
          )}
        </p>

        {q.isPending ? (
          <Card title="The program">
            <Skeleton rows={6} />
          </Card>
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => void q.refetch()} />
        ) : !status || !p ? null : (
          <>
            <div className={s.tiles}>
              <Tile
                v={`${p.live}/${p.total}`}
                l={t("screens served by the platform")}
                sub={t("{pct}% of those declared", { pct: p.livePct })}
              />
              <Tile
                v={pct(p.agreementPct)}
                l={t("agreement while shadowing")}
                sub={t("{n} run(s) recorded", { n: p.observations.toLocaleString(locale()) })}
              />
              <Tile
                v={String(p.untriaged)}
                l={t("differences not yet triaged")}
                sub={p.untriaged ? t("each blocks a cutover") : t("nothing waiting")}
              />
              <Tile
                v={String(p.readyNow.length)}
                l={t("ready to cut over")}
                sub={p.readyNow.length ? p.readyNow.map((x) => x.name).join(", ") : t("none today")}
              />
              <Tile
                v={p.worstP95Ms == null ? "—" : `${p.worstP95Ms} ms`}
                l={t("slowest shadow answer")}
                sub={t("95th percentile, what a user would feel")}
              />
            </div>

            <Card title="What the fraction means" sub="In the platform's own words">
              <p className={s.lead}>{p.note}</p>
            </Card>

            <Card
              title="What to do next"
              sub="Triage, cut over ready screens, then add runs"
            >
              <div className={s.actions}>
                {p.actions.slice(0, 6).map((a, i) => (
                  <div key={a.screen} className={s.action}>
                    <span className={s.rank}>{i + 1}</span>
                    <span>
                      <button type="button" className={s.rowBtn} onClick={() => setOpen(a.screen)}>
                        <b>{a.what}</b> — {a.name}
                      </button>
                      <div className={s.why}>{a.why}</div>
                    </span>
                  </div>
                ))}
                {!p.actions.length && (
                  <div className="muted" style={{ fontSize: 13.5 }}>
                    {t("Every declared screen is live.")}
                  </div>
                )}
              </div>
            </Card>

            <Card
              title="The screens"
              sub="Each screen's progress and guard status"
            >
              <div>
                <div className={`${s.row} ${s.head}`}>
                  <span>{t("Screen")}</span>
                  <span>{t("Mode")}</span>
                  <span>{t("Progress")}</span>
                  <span className={s.num}>{t("Agreement")}</span>
                  <span className={s.num}>{t("Runs")}</span>
                  <span>{t("Blocking")}</span>
                </div>
                {[...status.screens]
                  .sort((a, b) => progress(b) - progress(a))
                  .map((sc) => (
                    <div key={sc.key} className={s.row}>
                      <button type="button" className={s.rowBtn} onClick={() => setOpen(sc.key)}>
                        <b>{sc.name}</b>
                        <div className={s.sub}>
                          {sc.legacyRoute ?? sc.key}
                          {sc.owner ? ` · ${sc.owner}` : ""}
                        </div>
                      </button>
                      <span className={`${s.mode} ${MODE_CLASS[sc.mode]}`}>{t(MODE_TAG[sc.mode])}</span>
                      <span
                        className={s.bar}
                        title={t("{pct}% of the way to a cutover", { pct: Math.round(progress(sc) * 100) })}
                      >
                        <span
                          style={{
                            width: `${Math.round(progress(sc) * 100)}%`,
                            background: ready(sc) ? "var(--go)" : sc.mode === "live" ? "var(--go)" : "var(--blue)",
                          }}
                        />
                      </span>
                      <span className={s.num}>{pct(sc.evidence.agreementPct)}</span>
                      <span className={s.num}>{sc.evidence.observations.toLocaleString(locale())}</span>
                      <span>
                        {sc.mode === "live" ? (
                          <span className={s.met} style={{ fontSize: 12 }}>
                            {sc.cutoverAt
                              ? t("served by the platform since {date}", { date: sc.cutoverAt.slice(0, 10) })
                              : t("served by the platform")}
                          </span>
                        ) : ready(sc) ? (
                          <span className={s.met} style={{ fontSize: 12 }}>
                            {t("nothing — the guard would allow it")}
                          </span>
                        ) : (
                          <ul className={s.blocking}>
                            {sc.blocking.slice(0, 2).map((b) => (
                              <li key={b}>{b}</li>
                            ))}
                          </ul>
                        )}
                      </span>
                    </div>
                  ))}
              </div>
            </Card>

            <Card
              title="How the integration is installed"
              sub="One call, added once; later screens need no changes"
            >
              <ol style={{ fontSize: 13.5, margin: 0, paddingLeft: 20, display: "grid", gap: 6 }}>
                <li>{t("Compute the screen’s answer exactly as the existing application does today.")}</li>
                <li>{t("Post it to the platform with the screen key and the request parameters.")}</li>
                <li>{t("Return what comes back, not your own rows, and render it exactly as before.")}</li>
                <li>
                  {t(
                    "Fail open: on any error, timeout or refusal, return your own answer so a screen never breaks.",
                  )}
                </li>
              </ol>
            </Card>
          </>
        )}
      </div>

      {open && (
        <ScreenDrawer
          screenKey={open}
          onClose={() => setOpen(null)}
          onMode={(m, reason, force) => mode.mutateAsync({ key: open, mode: m, reason, force })}
        />
      )}
    </Page>
  );
}

function Tile({ v, l, sub }: { v: string; l: string; sub?: string }) {
  return (
    <div className={s.tile}>
      <div className={s.tileV}>{v}</div>
      <div className={s.tileL}>{l}</div>
      {sub && <div className={s.tileS}>{sub}</div>}
    </div>
  );
}

/** One screen: its contract, the four conditions measured, its differences, and the moves open to it. */
function ScreenDrawer({
  screenKey,
  onClose,
  onMode,
}: {
  screenKey: string;
  onClose: () => void;
  onMode: (
    m: ScreenMode,
    reason: string,
    force?: boolean,
  ) => Promise<{ from: ScreenMode; to: ScreenMode; forced: boolean; blocking?: string[] }>;
}) {
  const session = useSession();
  const source = session.source;
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [refused, setRefused] = useState<string[] | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const q = useQuery({ queryKey: ["migration-screen", screenKey], queryFn: () => source.migrationScreen(screenKey) });
  const d = useQuery({
    queryKey: ["migration-diffs", screenKey],
    queryFn: () => source.migrationDifferences(screenKey, true),
  });
  const triage = useMutation({
    mutationFn: (v: { id: number; triage: Triage }) => source.triageObservation(v.id, v.triage),
    onSuccess: (_r, v) => {
      setSaid(t("{label} — recorded", { label: t(TRIAGE_LABEL[v.triage]) }));
      void qc.invalidateQueries({ queryKey: ["migration-diffs", screenKey] });
      void qc.invalidateQueries({ queryKey: ["migration-screen", screenKey] });
      void qc.invalidateQueries({ queryKey: ["migration"] });
    },
  });

  const screen = q.data;
  const move = async (m: ScreenMode, force = false) => {
    setRefused(null);
    const r = await onMode(m, reason.trim() || "no reason given", force);
    if (r.blocking) setRefused(r.blocking);
    else {
      setSaid(
        r.forced
          ? t("{mode} — overridden, and the override is recorded", { mode: t(MODE_LABEL[r.to]) })
          : t(MODE_LABEL[r.to]),
      );
      void qc.invalidateQueries({ queryKey: ["migration-screen", screenKey] });
    }
  };

  return (
    <div className={s.drawerWrap} onClick={onClose}>
      <aside
        className={s.drawer}
        role="dialog"
        aria-label={t("{name} — migration", { name: screen?.name ?? screenKey })}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={s.drawerHead}>
          <h2>{screen?.name ?? screenKey}</h2>
          {screen && <span className={`${s.mode} ${MODE_CLASS[screen.mode]}`}>{t(MODE_TAG[screen.mode])}</span>}
          <Button size="sm" onClick={onClose}>
            {t("Close")}
          </Button>
        </div>

        {q.isPending ? (
          <Skeleton rows={8} />
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => void q.refetch()} />
        ) : !screen ? null : (
          <>
            <p className={s.lead}>{screen.description}</p>

            <section>
              <div className="eyebrow">{t("The four conditions")}</div>
              <p className={s.sub} style={{ margin: "4px 0 8px" }}>
                {t("The platform decides. These show how far each condition is from passing.")}
              </p>
              <div className={s.gaps}>
                {gaps(screen).map((g) => (
                  <div key={g.key} className={s.gap}>
                    <span className={g.met ? s.met : s.unmet}>
                      {g.met ? t("met") : t("not yet")} · {g.want}
                    </span>
                    <span className={s.bar}>
                      <span
                        style={{
                          width: `${Math.round((g.progress ?? 0) * 100)}%`,
                          background: g.met ? "var(--go)" : "var(--caution)",
                        }}
                      />
                    </span>
                    <span className={s.mono} style={{ fontSize: 12 }}>
                      {g.have}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="eyebrow">
                {t("Evidence · {days} days, contract v{version}", {
                  days: screen.evidence.days,
                  version: screen.evidence.contractVersion,
                })}
              </div>
              <div className={s.sub} style={{ marginTop: 4 }}>
                {Object.entries(screen.evidence.byVerdict)
                  .filter(([, n]) => n)
                  .map(
                    ([v, n]) =>
                      `${VERDICT_LABEL[v as keyof typeof VERDICT_LABEL] ? t(VERDICT_LABEL[v as keyof typeof VERDICT_LABEL]) : v}: ${n}`,
                  )
                  .join(" · ")}
                {screen.evidence.platformP95Ms != null
                  ? t(" · platform p95 {ms} ms", { ms: screen.evidence.platformP95Ms })
                  : ""}
              </div>
              <div className={s.sub}>
                {t(
                  "Agreement counts only what the contract can compare, so comparing nothing never shows 100%. This screen needs {pct}%.",
                  { pct: required(screen) },
                )}
              </div>
              {Object.keys(screen.evidence.triage).length > 0 && (
                <div className={s.sub}>
                  {t("Triaged: {list}", {
                    list: Object.entries(screen.evidence.triage)
                      .map(([k, n]) => `${TRIAGE_LABEL[k as Triage] ? t(TRIAGE_LABEL[k as Triage]) : k} ${n}`)
                      .join(" · "),
                  })}
                </div>
              )}
            </section>

            <section>
              <div className="eyebrow">{t("Differences waiting · {n}", { n: d.data?.length ?? 0 })}</div>
              {d.isPending ? (
                <Skeleton rows={3} />
              ) : !d.data?.length ? (
                <p className={s.sub} style={{ marginTop: 6 }}>
                  {t("Nothing waiting. Every difference has been reviewed.")}
                </p>
              ) : (
                <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
                  {d.data.map((x) => (
                    <div key={x.id} className={s.diff}>
                      <div className={s.mono} style={{ fontSize: 12 }}>
                        {x.observedAt.slice(0, 16).replace("T", " ")} · {t(VERDICT_LABEL[x.verdict])} · {x.requestKey}
                      </div>
                      <div style={{ fontSize: 13.5 }}>{x.detail}</div>
                      <div className={s.sides}>
                        <div className={s.side}>
                          <div className={s.sideL}>{t("The existing screen")}</div>
                          <pre>{JSON.stringify(x.legacyRows, null, 1)}</pre>
                        </div>
                        <div className={s.side}>
                          <div className={s.sideL}>{t("This platform")}</div>
                          <pre>{JSON.stringify(x.platformRows, null, 1)}</pre>
                        </div>
                      </div>
                      <div className={s.triage}>
                        <span className={s.sub}>{t("Which side was wrong?")}</span>
                        {(Object.keys(TRIAGE_LABEL) as Triage[]).map((k) => (
                          <Button key={k} size="sm" onClick={() => void triage.mutateAsync({ id: x.id, triage: k })}>
                            {t(TRIAGE_LABEL[k])}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className={s.sub}>
                    {t(
                      "You decide, not the platform. Often the existing screen is the one that's wrong.",
                    )}
                  </p>
                </div>
              )}
            </section>

            <section>
              <div className="eyebrow">{t("The contract")}</div>
              <p className={s.sub} style={{ margin: "4px 0 6px" }}>
                {t(
                  "A contract names a public endpoint and how to read it. It can't hold a query; if the public API can't serve a screen, the API isn't finished.",
                )}
              </p>
              <pre className={s.contract}>
                {JSON.stringify(
                  { source: screen.source, projection: screen.projection, comparison: screen.comparison },
                  null,
                  1,
                )}
              </pre>
            </section>

            <section>
              <div className="eyebrow">{t("Move this screen")}</div>
              <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("why — recorded with the change")}
                  aria-label={t("Reason")}
                />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {screen.mode !== "shadow" && (
                    <Button onClick={() => void move("shadow")}>{t("Shadow run it")}</Button>
                  )}
                  {screen.mode !== "live" && (
                    <Button variant="primary" onClick={() => void move("live")}>
                      {t("Cut over to the platform")}
                    </Button>
                  )}
                  {screen.mode === "live" && (
                    <Button variant="danger" onClick={() => void move("shadow")}>
                      {t("Revert to the existing screen")}
                    </Button>
                  )}
                  {screen.mode !== "off" && <Button onClick={() => void move("off")}>{t("Stop migrating it")}</Button>}
                </div>
                {refused && (
                  <div className={s.refused}>
                    <b>{t("This screen has not earned a cutover yet.")}</b>
                    <ul className={s.blocking} style={{ marginTop: 4 }}>
                      {refused.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                    <div style={{ marginTop: 6 }}>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => void move("live", true)}
                        disabled={!reason.trim()}
                      >
                        {t("Override with this reason")}
                      </Button>
                      <span className={s.sub} style={{ marginLeft: 8 }}>
                        {reason.trim()
                          ? t("the override is recorded against your name")
                          : t("an override needs a written reason")}
                      </span>
                    </div>
                  </div>
                )}
                {said && <div className={s.saved}>{said}</div>}
                <p className={s.sub}>
                  {t(
                    "Reverting is never blocked. It takes one call and no deploy, which is what makes a cutover safe to try.",
                  )}
                </p>
              </div>
            </section>

            {screen.history.length > 0 && (
              <section>
                <div className="eyebrow">{t("What has happened to it")}</div>
                <div className={s.hist} style={{ marginTop: 6 }}>
                  {screen.history.map((h, i) => (
                    <div key={i} className={s.histRow}>
                      <span className={s.mono} style={{ fontSize: 12 }}>
                        {h.at.slice(0, 16).replace("T", " ")}
                      </span>
                      <span>
                        {t(MODE_LABEL[h.fromMode])} → <b>{t(MODE_LABEL[h.toMode])}</b>
                        {h.forced ? t(" (overridden)") : ""}
                      </span>
                      <span className={s.sub}>{h.reason}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </aside>
    </div>
  );
}

export type { MigrationScreen };
